/**
 * Shared Battery Status reader for Timbrica tools — `window.__batteryStatus`.
 *
 * WHY THIS EXISTS (the trap every naive getBattery() call site fell into):
 *
 * The Battery Status spec says that when a browser cannot — or for privacy
 * will not — report the real battery, the BatteryManager keeps its DEFAULT
 * slots, which "emulate a fully charged and plugged in battery":
 *
 *     level = 1.0 · charging = true · chargingTime = 0 · dischargingTime = ∞
 *
 * That quad is byte-identical to a genuinely full device on a charger, so a
 * single read cannot tell "100%" from "I refuse to tell you". Tools that
 * printed `Math.round(b.level*100) + '%'` therefore confidently displayed a
 * FAKE 100% — and worse, scored points for it.
 *
 * Measured on prod (30 d, /battery-checker `suspect_default` telemetry):
 *   YaBrowser Android  230 default / 8 real  → 96.6% fake (+81 sessions with
 *                                              no Battery API at all)
 *   Chrome  Android    238 / 204             → 54%
 *   desktops           mixed                 → also covers "PC has no battery"
 * Reported twice by users: 2026-06-22 (/battery-checker) and 2026-07-24
 * (comment #1145 on /system-diagnostics) — the second one only because the
 * fix lived inside battery-checker.blade.php instead of a shared module.
 *
 * CONTRACT — three states, never "a number you can trust blindly":
 *   'ok'          real reading; level/percent are trustworthy
 *   'unconfirmed' the spec-default quad came back: EITHER truly full+plugged,
 *                 OR a desktop without a battery, OR the browser is hiding it.
 *                 Show the number if you like, but LABEL it, and never award
 *                 score points or arm a low-battery guard on it.
 *   'unsupported' no API (Firefox/Safari removed it) or the promise rejected
 *
 * WHAT `charging` MEANS. The plug, not the current: Chromium sets it from the
 * AC line on Windows and from `plugged != 0` on Android, so a laptop held at
 * its charge limit reports `charging: true` for as long as it is plugged in.
 * Label it with tools.battery.plugged_in. "Charging" is for a tool that has
 * watched the level rise (BatteryLive.plug, battery-live.js), and an unknown
 * `chargingTime` / `dischargingTime` is Infinity — "not known", never a time
 * (BatteryLive.reported). docs/why/battery-checker.md.
 *
 * USAGE
 *   const r = await window.__batteryStatus.read();
 *   if (r.state === 'ok') { …r.percent… }
 *   window.__batteryStatus.onChange(function (r) { … });   // re-render/re-score
 *
 * `onChange` matters: an 'unconfirmed' reading self-upgrades to 'ok' the moment
 * any non-default value arrives (unplug, drain, a late fill-in), so a notice can
 * retract itself instead of libelling a browser that was merely slow.
 *
 * Telemetry (once per page, deduped): tool_diagnostic{issue:battery_unconfirmed}
 * or tool_blocked{reason:no_battery_api|getbattery_rejected}.
 */
(function () {
'use strict';

var STATE_OK = 'ok', STATE_UNCONFIRMED = 'unconfirmed', STATE_UNSUPPORTED = 'unsupported';

var _promise = null;      // single in-flight/settled read — the API is page-global
var _manager = null;      // BatteryManager once resolved
var _reading = null;      // last computed reading
var _listeners = [];
var _proven = false;      // a non-default value was seen → trust the API for good
var _reported = false;    // telemetry emitted once per page

/** The exact "emulate a fully charged and plugged in battery" signature. */
function isDefaultQuad(b) {
    return b.level === 1 && b.charging === true &&
           b.chargingTime === 0 && !isFinite(b.dischargingTime);
}

function build(b) {
    var unconfirmed = !_proven && isDefaultQuad(b);
    if (!unconfirmed) _proven = true;
    return {
        state: unconfirmed ? STATE_UNCONFIRMED : STATE_OK,
        supported: true,
        confirmed: !unconfirmed,
        level: b.level,
        percent: Math.round(b.level * 100),
        charging: b.charging,
        chargingTime: b.chargingTime,
        dischargingTime: b.dischargingTime,
        manager: b,
        reason: unconfirmed ? 'default_quad' : null,
    };
}

function unsupported(reason) {
    return {
        state: STATE_UNSUPPORTED, supported: false, confirmed: false,
        level: null, percent: null, charging: null,
        chargingTime: null, dischargingTime: null, manager: null, reason: reason,
    };
}

/* Telemetry helpers are defined by tier-0 AFTER pushed tool scripts run, so a
 * load-time emit would be silently dropped (the hole found on battery-checker
 * 2026-06-23). Defer until __ae exists, then give up quietly. */
function whenAe(fn) {
    if (window.__ae) { try { fn(); } catch (e) {} return; }
    var tries = 0;
    var iv = setInterval(function () {
        if (window.__ae) { clearInterval(iv); try { fn(); } catch (e) {} }
        else if (++tries > 40) clearInterval(iv);
    }, 150);
}

function report(r) {
    if (_reported) return;
    _reported = true;
    whenAe(function () {
        if (r.state === STATE_UNSUPPORTED) {
            window.__ae('tool_blocked', {
                reason: r.reason === 'rejected' ? 'getbattery_rejected' : 'no_battery_api',
            });
        } else if (r.state === STATE_UNCONFIRMED) {
            window.__aeDiagnostic
                ? window.__aeDiagnostic({ issue: 'battery_unconfirmed' })
                : window.__ae('tool_diagnostic', { issue: 'battery_unconfirmed' });
        }
    });
}

function emit(r) {
    _reading = r;
    for (var i = 0; i < _listeners.length; i++) {
        try { _listeners[i](r); } catch (e) {}
    }
}

function subscribe(b) {
    // Firefox-Android era polyfills expose a BatteryManager without listeners.
    if (typeof b.addEventListener !== 'function') return;
    ['levelchange', 'chargingchange', 'chargingtimechange', 'dischargingtimechange'].forEach(function (ev) {
        try {
            b.addEventListener(ev, function () {
                var prev = _reading && _reading.state;
                var next = build(b);
                emit(next);
                // First time real data disproves the default quad, say so once.
                if (prev === STATE_UNCONFIRMED && next.state === STATE_OK) {
                    whenAe(function () {
                        window.__ae('tool_diagnostic', { issue: 'battery_confirmed_late' });
                    });
                }
            });
        } catch (e) {}
    });
}

var api = {
    STATE_OK: STATE_OK,
    STATE_UNCONFIRMED: STATE_UNCONFIRMED,
    STATE_UNSUPPORTED: STATE_UNSUPPORTED,

    /** @returns {Promise<Object>} never rejects — inspect `.state`. */
    read: function () {
        if (_promise) return _promise;
        _promise = new Promise(function (resolve) {
            if (!navigator.getBattery || typeof navigator.getBattery !== 'function') {
                var u = unsupported('no_api');
                emit(u); report(u); resolve(u);
                return;
            }
            var p;
            try { p = navigator.getBattery(); } catch (e) { p = null; }
            if (!p || typeof p.then !== 'function') {
                var u2 = unsupported('rejected');
                emit(u2); report(u2); resolve(u2);
                return;
            }
            p.then(function (b) {
                _manager = b;
                var r = build(b);
                subscribe(b);
                emit(r); report(r); resolve(r);
            }).catch(function () {
                var u3 = unsupported('rejected');
                emit(u3); report(u3); resolve(u3);
            });
        });
        return _promise;
    },

    /** Latest reading without waiting (null before the first read()). */
    current: function () { return _reading; },

    /** Re-render/re-score hook. Fires on every change, incl. unconfirmed→ok. */
    onChange: function (cb) {
        if (typeof cb === 'function') _listeners.push(cb);
    },

    /** Exposed for the guard test and for tools that already hold a manager. */
    isDefaultQuad: isDefaultQuad,
};

window.__batteryStatus = api;
})();
